import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail,
  Phone,
  Wallet,
  Gamepad2,
  Ticket,
  Plus,
  Trash2,
  LogOut,
  PenSquare,
  CheckCircle2,
  Star,
  User as UserIcon,
  RotateCw,
  Clock,
  Ban,
  Store,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  X,
} from 'lucide-react';
import { useUIStore } from '../../stores/useUIStore';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';
import { useCurrencyStore } from '../../stores/useCurrencyStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { useCartStore } from '../../stores/useCartStore';
import { orderApi, OrderDetail } from '../../services/orderApi';
import { EditProfileModal } from './EditProfileModal';
import { AccountStatusModal } from './AccountStatusModal';
import { checkFriendshipStatus, FriendshipCheckResponse } from '../../services/checkApi';

export interface UserCoupon {
  id: number;
  code: string;
  title?: string;
  discount_type?: 'PERCENTAGE' | 'FIXED';
  discount_value?: number;
  discount_pct?: number;
  currency?: string;
  min_vbucks?: number;
  expires_at?: string | null;
  is_used?: boolean;
  used_at?: string | null;
  is_expired?: boolean;
  can_use?: boolean;
  source?: string;
  created_at?: string | null;
}

export interface LinkedAccount {
  id?: number;
  username: string;
  platform: string;
  is_primary: boolean;
  fortnite_account_id?: string;
}

export const ProfilePage: React.FC = () => {
  const openDeposit = useUIStore((s) => s.openDepositModal);
  const openLinkAccount = useUIStore((s) => s.openLinkAccountModal);
  const openAuth = useUIStore((s) => s.openAuthModal);
  const navigateTo = useUIStore((s) => s.navigateTo);

  const currency = useCurrencyStore((s) => s.currency);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const fetchMe = useAuthStore((s) => s.fetchMe);

  // Coupons State
  const [coupons, setCoupons] = useState<UserCoupon[]>([]);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);

  const loadCoupons = useCallback(async () => {
    const token =
      localStorage.getItem('fn_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('auth_token');
    if (!token) {
      setCoupons([]);
      return;
    }
    setIsLoadingCoupons(true);
    try {
      const res = await fetch('/api/coupons/my-coupons', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCoupons(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.warn('[PROFILE] Error fetching coupons:', err);
    } finally {
      setIsLoadingCoupons(false);
    }
  }, []);

  // Sync live profile (balance/G-Coins) and user coupons on mount
  useEffect(() => {
    fetchMe();
    loadCoupons();
  }, [fetchMe, loadCoupons]);

  // Orders State
  const [orders, setOrders] = useState<OrderDetail[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [cancellingCode, setCancellingCode] = useState<string | null>(null);
  const [visibleOrdersCount, setVisibleOrdersCount] = useState<number>(3);

  const loadOrders = async () => {
    if (!user) return;
    setIsLoadingOrders(true);
    try {
      const res = await orderApi.getMyOrders();
      setOrders(res);
    } catch {
      setOrders([]);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadOrders();
    }
  }, [user]);

  const handleCancelOrder = async (orderCode: string) => {
    const confirmed = window.confirm(
      `¿Estás seguro de que deseas cancelar el pedido ${orderCode}?`
    );
    if (!confirmed) return;

    setCancellingCode(orderCode);
    try {
      await orderApi.cancelOrder(orderCode);
      await loadOrders();
    } catch (err: any) {
      alert(err.message || 'Error al cancelar el pedido.');
    } finally {
      setCancellingCode(null);
    }
  };

  // Linked Accounts
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>(() => {
    try {
      const raw = localStorage.getItem('fn_linked_accounts');
      if (raw) return JSON.parse(raw);
      if (user?.fortnite_username) {
        const initial = [
          {
            username: user.fortnite_username,
            platform: 'Epic Games',
            is_primary: true,
          },
        ];
        localStorage.setItem('fn_linked_accounts', JSON.stringify(initial));
        return initial;
      }
      return [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const handleSync = () => {
      try {
        const rawAccs = localStorage.getItem('fn_linked_accounts');
        if (rawAccs) setLinkedAccounts(JSON.parse(rawAccs));
      } catch {}
    };

    window.addEventListener('storage', handleSync);
    window.addEventListener('accountsChanged' as any, handleSync);
    return () => {
      window.removeEventListener('storage', handleSync);
      window.removeEventListener('accountsChanged' as any, handleSync);
    };
  }, []);

  // Modal & Account Actions State
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [selectedAccountForStatus, setSelectedAccountForStatus] = useState<LinkedAccount | null>(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [accountStatuses, setAccountStatuses] = useState<
    Record<string, { loading: boolean; data?: FriendshipCheckResponse; error?: string }>
  >({});
  const [settingDefaultId, setSettingDefaultId] = useState<number | null>(null);
  const [accountToUnlink, setAccountToUnlink] = useState<LinkedAccount | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [singleAccountWarningOpen, setSingleAccountWarningOpen] = useState(false);

  useEffect(() => {
    if (accountToUnlink || singleAccountWarningOpen) {
      lockScroll();
      return () => {
        unlockScroll();
      };
    }
  }, [accountToUnlink, singleAccountWarningOpen]);

  // Fetch accounts from backend on mount or when user changes
  useEffect(() => {
    const token =
      localStorage.getItem('fn_token') ||
      localStorage.getItem('token');
    if (token && user) {
      fetch('/api/auth/me/fortnite-accounts', {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (Array.isArray(data) && data.length > 0) {
            const mapped: LinkedAccount[] = data.map((acc: any) => ({
              id: acc.id,
              username: acc.fortnite_username,
              platform: 'Epic Games',
              is_primary: Boolean(acc.is_default),
              fortnite_account_id: acc.fortnite_account_id,
            }));
            setLinkedAccounts(mapped);
            localStorage.setItem('fn_linked_accounts', JSON.stringify(mapped));
          }
        })
        .catch(() => {});
    }
  }, [user]);

  // Status badge check per account
  const checkAccountBadge = useCallback(async (acc: LinkedAccount) => {
    setAccountStatuses((prev) => ({
      ...prev,
      [acc.username]: { loading: true },
    }));

    try {
      const res = await checkFriendshipStatus(
        {
          username: acc.username,
          account_id: acc.fortnite_account_id || null,
          total_vbucks: 1000,
        },
        { timeoutMs: 10000 }
      );
      setAccountStatuses((prev) => ({
        ...prev,
        [acc.username]: { loading: false, data: res },
      }));
    } catch (err: any) {
      setAccountStatuses((prev) => ({
        ...prev,
        [acc.username]: { loading: false, error: err.message || 'Error al verificar' },
      }));
    }
  }, []);

  // Check statuses on accounts list changes
  useEffect(() => {
    if (linkedAccounts.length > 0) {
      linkedAccounts.forEach((acc) => {
        checkAccountBadge(acc);
      });
    }
  }, [linkedAccounts.map((a) => a.username).join(','), checkAccountBadge]);

  const handleOpenAccountStatus = (acc: LinkedAccount) => {
    setSelectedAccountForStatus(acc);
    setIsStatusModalOpen(true);
  };

  const handleSetDefaultAccount = async (targetAcc: LinkedAccount) => {
    const token =
      localStorage.getItem('fn_token') ||
      localStorage.getItem('token');
    if (!token) {
      openAuth('login');
      return;
    }

    let accountId = targetAcc.id;
    if (!accountId) {
      try {
        const res = await fetch('/api/auth/me/fortnite-accounts', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const freshList = await res.json();
          const found = freshList.find(
            (a: any) => a.fortnite_username.toLowerCase() === targetAcc.username.toLowerCase()
          );
          if (found) accountId = found.id;
        }
      } catch {}
    }

    if (!accountId) return;

    setSettingDefaultId(accountId);

    try {
      const res = await fetch(`/api/auth/me/fortnite-accounts/${accountId}/default`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Error al cambiar cuenta predeterminada');
      }

      // Optimistic update of local linkedAccounts list
      const updatedAccounts = linkedAccounts.map((a) => ({
        ...a,
        is_primary: a.id === accountId || a.username.toLowerCase() === targetAcc.username.toLowerCase(),
      }));

      // Sort to keep primary account at the top
      updatedAccounts.sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));
      setLinkedAccounts(updatedAccounts);
      localStorage.setItem('fn_linked_accounts', JSON.stringify(updatedAccounts));

      // Update user in auth store
      if (user) {
        useAuthStore.getState().setUser({
          ...user,
          fortnite_username: targetAcc.username,
        });
      }

      // Re-sync full user from server
      const meRes = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (meRes.ok) {
        const freshUser = await meRes.json();
        useAuthStore.getState().setUser(freshUser);
      }

      // Notify app components
      window.dispatchEvent(new Event('accountsChanged'));
      window.dispatchEvent(new Event('userChanged'));
    } catch (err: any) {
      alert(err.message || 'Error al establecer la cuenta como principal.');
    } finally {
      setSettingDefaultId(null);
    }
  };

  const handleInitiateUnlink = (acc: LinkedAccount) => {
    // Guard against deleting only linked account
    if (linkedAccounts.length <= 1) {
      setSingleAccountWarningOpen(true);
      return;
    }
    setAccountToUnlink(acc);
  };

  const handleConfirmUnlink = async () => {
    if (!accountToUnlink) return;

    const token =
      localStorage.getItem('fn_token') ||
      localStorage.getItem('token');
    if (!token) {
      openAuth('login');
      setAccountToUnlink(null);
      return;
    }

    let accountId = accountToUnlink.id;
    if (!accountId) {
      try {
        const res = await fetch('/api/auth/me/fortnite-accounts', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const freshList = await res.json();
          const found = freshList.find(
            (a: any) => a.fortnite_username.toLowerCase() === accountToUnlink.username.toLowerCase()
          );
          if (found) accountId = found.id;
        }
      } catch {}
    }

    if (!accountId) {
      const filtered = linkedAccounts.filter(
        (a) => a.username.toLowerCase() !== accountToUnlink.username.toLowerCase()
      );
      setLinkedAccounts(filtered);
      localStorage.setItem('fn_linked_accounts', JSON.stringify(filtered));
      setAccountToUnlink(null);
      return;
    }

    setIsUnlinking(true);
    try {
      const res = await fetch(`/api/auth/me/fortnite-accounts/${accountId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 400 && data.detail?.includes('única cuenta')) {
          setAccountToUnlink(null);
          setSingleAccountWarningOpen(true);
          return;
        }
        throw new Error(data.detail || 'Error al eliminar la cuenta de Fortnite.');
      }

      // Refresh accounts from server
      const accsRes = await fetch('/api/auth/me/fortnite-accounts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (accsRes.ok) {
        const freshAccs = await accsRes.json();
        const mapped: LinkedAccount[] = freshAccs.map((a: any) => ({
          id: a.id,
          username: a.fortnite_username,
          fortnite_account_id: a.fortnite_account_id,
          platform: 'Epic Games',
          is_primary: Boolean(a.is_default),
        }));
        setLinkedAccounts(mapped);
        localStorage.setItem('fn_linked_accounts', JSON.stringify(mapped));
      } else {
        const filtered = linkedAccounts.filter((a) => a.id !== accountId);
        setLinkedAccounts(filtered);
        localStorage.setItem('fn_linked_accounts', JSON.stringify(filtered));
      }

      // Refresh user state
      const meRes = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (meRes.ok) {
        const freshUser = await meRes.json();
        useAuthStore.getState().setUser(freshUser);
      }

      window.dispatchEvent(new Event('accountsChanged'));
      window.dispatchEvent(new Event('userChanged'));
      setAccountToUnlink(null);
    } catch (err: any) {
      alert(err.message || 'Error al eliminar la cuenta.');
    } finally {
      setIsUnlinking(false);
    }
  };

  const renderStatusBadge = (statusInfo?: {
    loading: boolean;
    data?: FriendshipCheckResponse;
    error?: string;
  }) => {
    if (!statusInfo || statusInfo.loading) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 border border-slate-200 text-slate-500 text-[11px] font-bold font-heading">
          <RotateCw className="w-3 h-3 animate-spin text-[#1447e6]" />
          <span>Verificando estado...</span>
        </span>
      );
    }

    if (statusInfo.error) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-50 border border-red-200 text-red-600 text-[11px] font-bold font-heading">
          <AlertCircle className="w-3 h-3 text-red-500" />
          <span>Error al verificar</span>
        </span>
      );
    }

    const data = statusInfo.data;
    if (!data) return null;

    const pendingCount = data.pending_bots_count ?? 0;
    if (pendingCount > 0 || data.status === 'REQUEST_SENT') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-bold font-heading">
          <Clock className="w-3 h-3 text-amber-600" />
          <span>Solicitudes sin aceptar {pendingCount > 0 ? `(${pendingCount})` : ''}</span>
        </span>
      );
    }

    if (data.status === 'FRIENDS_READY' || (data.bots_ready_count && data.bots_ready_count > 0)) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-bold font-heading">
          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
          <span>Listo (+48hs)</span>
        </span>
      );
    }

    if (data.status === 'FRIENDS_WAITING_48H' || (data.bots_waiting_count && data.bots_waiting_count > 0)) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-bold font-heading">
          <Clock className="w-3 h-3 text-amber-600" />
          <span>Faltan ~{data.hours_remaining ?? 48} hs</span>
        </span>
      );
    }

    if (data.status === 'NOT_FRIENDS') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-50 border border-red-200 text-red-700 text-[11px] font-bold font-heading">
          <AlertTriangle className="w-3 h-3 text-red-500" />
          <span>Sin bots</span>
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 text-blue-800 text-[11px] font-bold font-heading">
        <CheckCircle2 className="w-3 h-3 text-blue-600" />
        <span>{data.message || 'Verificada'}</span>
      </span>
    );
  };

  // Smooth scroll to target sections via hash (#billetera, #vincular-cuentas, #cupones, #pedidos)
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      let targetId = '';
      if (hash === '#billetera' || hash === '#saldo' || hash === '#mi-saldo' || hash === '#saldo-section') targetId = 'billetera';
      else if (hash === '#vincular-cuentas' || hash === '#cuentas' || hash === '#cuentas-fortnite') targetId = 'vincular-cuentas';
      else if (hash === '#cupones' || hash === '#mis-cupones' || hash === '#coupons-section') targetId = 'cupones';
      else if (hash === '#pedidos' || hash === '#mis-pedidos' || hash === '#orders-section' || hash === '#historial') targetId = 'pedidos';

      if (targetId) {
        setTimeout(() => {
          const el = document.getElementById(targetId) || document.getElementById('pedidos') || document.getElementById('orders-section');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            el.classList.add('ring-2', 'ring-[var(--royal)]', 'transition-all', 'duration-500');
            setTimeout(() => el.classList.remove('ring-2', 'ring-[var(--royal)]'), 2500);
          }
        }, 120);
      }
    };

    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  const handleLogout = () => {
    logout();
  };

  if (!user) {
    return (
      <div className="w-full min-h-screen bg-transparent text-[#06173f] py-12 px-4 sm:px-6">
        <div className="max-w-[500px] mx-auto">
          <div
            className="p-8 bg-white border-2 border-[var(--line)] shadow-sm text-center space-y-5"
            style={{
              clipPath:
                'polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px)',
            }}
          >
            <div
              className="w-16 h-16 mx-auto bg-[#eff6ff] text-[#1447e6] border-2 border-[#bfdbfe] flex items-center justify-center font-black text-2xl"
              style={{
                clipPath:
                  'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
              }}
            >
              <UserIcon className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-[#06173f] font-heading">
                Inicia sesión en tu cuenta
              </h2>
              <p className="text-xs sm:text-sm text-[var(--soft)] font-medium mt-1">
                Accede a tu perfil para consultar tu saldo de G-Coins, administrar tus cuentas de Fortnite vinculadas y ver tus pedidos.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => openAuth('login')}
                className="w-full sm:w-auto px-6 py-3 bg-white hover:bg-slate-50 text-[#1447e6] border-2 border-[var(--line)] font-heading font-black text-xs uppercase tracking-wider transition cursor-pointer"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                }}
              >
                Iniciar Sesión
              </button>
              <button
                type="button"
                onClick={() => openAuth('register')}
                className="w-full sm:w-auto px-6 py-3 bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black text-xs uppercase tracking-wider transition cursor-pointer shadow-md"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                }}
              >
                Crear Cuenta
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const displayName = user.fortnite_username
    ? user.fortnite_username.toUpperCase()
    : `${user.first_name || ''} ${user.last_name || ''}`.trim().toUpperCase() || 'USUARIO';

  const initials = user.first_name && user.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    : (user.fortnite_username ? user.fortnite_username.slice(0, 2).toUpperCase() : 'GF');

  return (
    <div className="w-full min-h-screen bg-transparent text-[#06173f] py-8 sm:py-12 px-4 sm:px-6">
      <div className="max-w-[1200px] mx-auto space-y-6 sm:space-y-8">
        {/* TARJETA 1: DATOS DEL USUARIO (IMAGEN 5) */}
        <div
          className="p-5 sm:p-6 bg-white border-2 border-[var(--line)] shadow-sm space-y-4"
          style={{
            clipPath:
              'polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px)',
          }}
        >
          {/* Fila Superior: Avatar + Nombre + Botones de Acción */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3.5 min-w-0">
              <div
                className="w-12 h-12 bg-[#eff6ff] text-[#1447e6] border-2 border-[#bfdbfe] flex items-center justify-center font-heading font-black text-lg shrink-0"
                style={{
                  clipPath:
                    'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
                }}
              >
                <span>{initials}</span>
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-[#06173f] font-heading truncate leading-tight">
                  {displayName}
                </h1>
                <p className="text-xs text-[var(--soft)] font-medium">Mi Cuenta</p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setIsEditProfileOpen(true)}
                className="w-9 h-9 flex items-center justify-center bg-white border-2 border-[var(--line)] text-[#1447e6] hover:bg-slate-50 transition cursor-pointer"
                style={{
                  clipPath:
                    'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                }}
                title="Editar Perfil"
                aria-label="Editar Perfil"
              >
                <PenSquare className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={handleLogout}
                className="w-9 h-9 flex items-center justify-center bg-red-50 border-2 border-red-200 text-red-600 hover:bg-red-100 transition cursor-pointer"
                style={{
                  clipPath:
                    'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                }}
                title="Cerrar Sesión"
                aria-label="Cerrar Sesión"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Fila Inferior: Email, Teléfono, Moneda */}
          <div className="border-t-2 border-[var(--line)] pt-3.5 space-y-3">
            {/* Correo */}
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-8 h-8 bg-[#eff6ff] text-[#1447e6] flex items-center justify-center shrink-0 text-sm"
                style={{
                  clipPath:
                    'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
                }}
              >
                <Mail className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 leading-none mb-1">
                  <span className="text-[10px] font-bold text-[var(--soft)] uppercase tracking-wider font-heading leading-none">
                    CORREO
                  </span>
                  <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-0.5 leading-none">
                    <CheckCircle2 className="w-3 h-3" /> verificado
                  </span>
                </div>
                <span className="text-xs font-semibold text-[#06173f] truncate block">
                  {user?.email || 'Sin correo'}
                </span>
              </div>
            </div>

            <div className="border-t border-[var(--line)] pt-3 flex items-center gap-3 min-w-0">
              <div
                className="w-8 h-8 bg-[#eff6ff] text-[#1447e6] flex items-center justify-center shrink-0 text-sm"
                style={{
                  clipPath:
                    'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
                }}
              >
                <Phone className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <span className="text-[10px] font-bold text-[var(--soft)] uppercase tracking-wider block font-heading mb-0.5">
                  TELÉFONO
                </span>
                <span className="text-xs font-semibold text-[#06173f] font-mono truncate block">
                  {user?.phone || 'Sin registrar'}
                </span>
              </div>
            </div>

            <div className="border-t border-[var(--line)] pt-3 flex items-center gap-3 min-w-0">
              <div
                className="w-8 h-8 bg-[#eff6ff] text-[#1447e6] flex items-center justify-center shrink-0 text-xs font-black font-heading"
                style={{
                  clipPath:
                    'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
                }}
              >
                {currency === 'MXN' ? 'MX' : 'AR'}
              </div>
              <div className="min-w-0">
                <span className="text-[10px] font-bold text-[var(--soft)] uppercase tracking-wider block font-heading mb-0.5">
                  MONEDA
                </span>
                <span className="text-xs font-black text-[#06173f] uppercase font-heading block">
                  {currency}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* GRID: BILLETERA + CUENTAS FORTNITE (1:1 con profile.html) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* TARJETA 2: BILLETERA */}
          <div
            id="billetera"
            className="p-5 sm:p-6 bg-white border-2 border-[var(--line)] shadow-sm space-y-4 scroll-mt-24"
            style={{
              clipPath:
                'polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px)',
            }}
          >
            <div className="flex items-center justify-between border-b-2 border-[var(--line)] pb-3">
              <h2 className="text-base font-black uppercase tracking-wider text-[#06173f] flex items-center gap-2 font-heading">
                <Wallet className="w-5 h-5 text-[#1447e6]" />
                <span>BILLETERA</span>
              </h2>
              <button
                type="button"
                onClick={() => openDeposit(2400)}
                className="bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black px-4 py-2 uppercase tracking-wider text-xs transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                }}
              >
                <Plus className="w-3.5 h-3.5 stroke-[3]" />
                <span>Cargar</span>
              </button>
            </div>

            {/* Saldo Principal */}
            <div
              className="p-4 bg-white border-2 border-[var(--line)]"
              style={{
                clipPath:
                  'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
              }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 bg-[#eff6ff] text-[#06173f] font-black italic flex items-center justify-center text-2xl shrink-0 font-heading border border-[#bfdbfe]"
                  style={{
                    clipPath:
                      'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                  }}
                >
                  G
                </div>
                <div className="min-w-0">
                  <span className="text-xs font-bold text-[var(--soft)] uppercase tracking-wider block font-heading">
                    G-COINS DISPONIBLES
                  </span>
                  <div className="flex items-baseline gap-1.5 mt-0.5">
                    <span className="text-3xl sm:text-4xl font-black text-[#1447e6] font-heading tracking-wide">
                      {(user?.balance_vbucks || 0).toLocaleString('es-AR')}
                    </span>
                    <span className="text-base font-black text-[#06173f] uppercase font-heading">
                      G
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-xs text-[var(--soft)] leading-relaxed font-medium">
              Tu saldo en <strong className="text-[#06173f]">G-Coins</strong> te permite comprar regalos en la tienda al instante con 1 clic, sin necesidad de transferir en cada compra.
            </p>
          </div>

          {/* TARJETA 3: CUENTAS FORTNITE */}
          <div
            id="vincular-cuentas"
            className="p-5 sm:p-6 bg-white border-2 border-[var(--line)] shadow-sm space-y-4 scroll-mt-24"
            style={{
              clipPath:
                'polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px)',
            }}
          >
            <div className="flex items-center justify-between border-b-2 border-[var(--line)] pb-3">
              <h2 className="text-base font-black uppercase tracking-wider text-[#06173f] flex items-center gap-2 font-heading">
                <Gamepad2 className="w-5 h-5 text-[#1447e6]" />
                <span>CUENTAS FORTNITE</span>
              </h2>
              <button
                type="button"
                onClick={openLinkAccount}
                className="bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black px-4 py-2 uppercase tracking-wider text-xs transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                }}
              >
                <Plus className="w-3.5 h-3.5 stroke-[3]" />
                <span>Vincular</span>
              </button>
            </div>

            <p className="text-xs text-[var(--soft)] leading-relaxed font-medium">
              Cuentas registradas para recibir regalos. Nuestros bots enviarán solicitud de amistad automáticamente.
            </p>

            {/* Lista de Cuentas */}
            <div className="space-y-2.5">
              {linkedAccounts.length === 0 ? (
                <div
                  className="text-center py-8 bg-[#f8fafc] border border-[var(--line)] p-6 space-y-2"
                  style={{
                    clipPath:
                      'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
                  }}
                >
                  <Gamepad2 className="w-8 h-8 text-slate-400 mx-auto" />
                  <p className="text-sm font-bold text-[#06173f] font-heading uppercase">
                    No tienes ninguna cuenta de Fortnite vinculada
                  </p>
                  <p className="text-xs text-[var(--soft)] font-medium max-w-sm mx-auto">
                    Agrega tu nombre de Epic Games para que nuestros bots puedan enviarte regalos.
                  </p>
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={openLinkAccount}
                      className="bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black px-4 py-2 uppercase tracking-wider text-xs transition shadow-sm cursor-pointer"
                      style={{
                        clipPath:
                          'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                      }}
                    >
                      <Plus className="w-3.5 h-3.5 inline mr-1 stroke-[3]" />
                      Vincular mi primera cuenta
                    </button>
                  </div>
                </div>
              ) : (
                linkedAccounts.map((acc) => {
                  const statusInfo = accountStatuses[acc.username];
                  return (
                    <div
                      key={acc.id ? `acc-${acc.id}` : acc.username}
                      className="p-3 sm:p-3.5 bg-white border-2 border-[var(--line)] space-y-2.5 shadow-xs transition hover:border-[#bfdbfe]"
                      style={{
                        clipPath:
                          'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                      }}
                    >
                      {/* Fila Superior: Icono + Username + Badges + Botones de Acción */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div
                            className="w-8 h-8 bg-[#eff6ff] text-[#1447e6] border border-[#bfdbfe] flex items-center justify-center text-xs shrink-0"
                            style={{
                              clipPath:
                                'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
                            }}
                          >
                            <Gamepad2 className="w-4 h-4" />
                          </div>

                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-heading font-black text-sm uppercase text-[#06173f] truncate">
                                {acc.username}
                              </span>

                              {acc.is_primary && (
                                <span
                                  className="px-2 py-0.5 bg-[#eff6ff] text-[#1447e6] border border-[#bfdbfe] text-[10px] font-black font-heading flex items-center gap-1 shrink-0"
                                  style={{
                                    clipPath:
                                      'polygon(3px 0, 100% 0, 100% calc(100% - 3px), calc(100% - 3px) 100%, 0 100%, 0 3px)',
                                  }}
                                >
                                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                                  <span>PRINCIPAL</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Botones de Acción */}
                        <div className="flex items-center gap-1.5 shrink-0">
                          {/* Botón 'Hacer cuenta principal' (Solo visible en cuentas no principales) */}
                          {!acc.is_primary && (
                            <button
                              type="button"
                              onClick={() => handleSetDefaultAccount(acc)}
                              disabled={settingDefaultId === acc.id}
                              className="w-8 h-8 flex items-center justify-center bg-white hover:bg-amber-50 text-slate-400 hover:text-amber-500 border border-[var(--line)] hover:border-amber-300 transition cursor-pointer disabled:opacity-50"
                              style={{
                                clipPath:
                                  'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
                              }}
                              title="Hacer cuenta principal"
                              aria-label="Hacer cuenta principal"
                            >
                              {settingDefaultId === acc.id ? (
                                <RotateCw className="w-3.5 h-3.5 animate-spin text-amber-500" />
                              ) : (
                                <Star className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}

                          {/* Botón 'Desvincular / Eliminar cuenta' */}
                          <button
                            type="button"
                            onClick={() => handleInitiateUnlink(acc)}
                            className={`w-8 h-8 flex items-center justify-center border transition cursor-pointer ${
                              linkedAccounts.length <= 1
                                ? 'bg-slate-50 text-slate-300 border-slate-200 hover:bg-slate-100 hover:text-slate-400'
                                : 'bg-red-50 hover:bg-red-100 text-red-600 border-red-200'
                            }`}
                            style={{
                              clipPath:
                                'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
                            }}
                            title={
                              linkedAccounts.length <= 1
                                ? 'No puedes eliminar tu única cuenta vinculada'
                                : 'Eliminar cuenta'
                            }
                            aria-label="Eliminar cuenta"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Fila Inferior: Estado de Amistad y Ver Estado */}
                      <div className="border-t border-[var(--line)] pt-2.5 flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenAccountStatus(acc)}
                          className="min-w-0 text-left cursor-pointer hover:opacity-85 transition"
                          title="Ver estado de amistad"
                        >
                          {renderStatusBadge(statusInfo)}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleOpenAccountStatus(acc)}
                          className="text-xs font-black font-heading uppercase text-[#1447e6] hover:text-[#0a2a9c] flex items-center gap-1 shrink-0 cursor-pointer transition"
                          title="Ver estado de amistad"
                        >
                          <span>Ver estado</span>
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* TARJETA 4: MIS CUPONES DE DESCUENTO */}
        <div
          id="cupones"
          data-section="coupons-section"
          className="p-5 sm:p-6 bg-white border-2 border-[var(--line)] shadow-sm space-y-4 scroll-mt-24"
          style={{
            clipPath:
              'polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px)',
          }}
        >
          <div className="flex items-center justify-between border-b-2 border-[var(--line)] pb-3">
            <h2 className="text-base font-black uppercase tracking-wider text-[#06173f] flex items-center gap-2 font-heading">
              <Ticket className="w-5 h-5 text-[#1447e6]" />
              <span>Mis Cupones de Descuento</span>
            </h2>
            <div className="flex items-center gap-2">
              <span
                className="px-2.5 py-0.5 bg-[#eff6ff] text-[#1447e6] border border-[#bfdbfe] text-xs font-black font-heading"
                style={{
                  clipPath:
                    'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
                }}
              >
                {coupons.filter((c) => c.can_use).length}
              </span>
              <button
                type="button"
                onClick={loadCoupons}
                disabled={isLoadingCoupons}
                className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-[var(--line)] text-slate-500 hover:text-[#06173f] transition cursor-pointer"
                style={{
                  clipPath:
                    'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
                }}
                title="Actualizar cupones"
              >
                <RotateCw className={`w-3.5 h-3.5 ${isLoadingCoupons ? 'animate-spin text-[#1447e6]' : ''}`} />
              </button>
            </div>
          </div>

          {isLoadingCoupons && coupons.length === 0 ? (
            <div className="py-6 flex flex-col items-center justify-center gap-2 text-slate-400">
              <div className="w-6 h-6 border-2 border-[var(--line)] border-t-[#1447e6] rounded-full animate-spin" />
              <span className="text-xs font-bold uppercase tracking-wider text-[var(--soft)] font-heading">
                Cargando cupones de tu cuenta...
              </span>
            </div>
          ) : coupons.length === 0 ? (
            <div
              className="p-6 bg-[#f8fafc] border border-[var(--line)] text-center space-y-2"
              style={{
                clipPath:
                  'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
              }}
            >
              <Ticket className="w-8 h-8 text-[#1447e6]/50 mx-auto" />
              <p className="font-heading font-black text-xs uppercase tracking-wide text-[#06173f]">
                Aún no tienes cupones acumulados en tu cuenta
              </p>
              <p className="text-[11px] text-[var(--soft)] max-w-md mx-auto leading-relaxed">
                Realiza una compra en la tienda y califícala con una foto de tus cosméticos recibidos para ganar un{' '}
                <strong className="text-[#06173f]">cupón del 10% OFF en tu 1ª reseña</strong> (y{' '}
                <strong className="text-[#06173f]">5% OFF</strong> en las siguientes).
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {coupons.map((c) => {
                const discountLabel =
                  c.discount_pct && c.discount_pct > 0
                    ? `${c.discount_pct}% OFF`
                    : c.discount_value
                    ? `$${c.discount_value.toLocaleString('es-AR')} OFF`
                    : 'Descuento';

                // Evitar repetición del descuento en el título (ej: "10% OFF - Recompensa" -> "Recompensa")
                const cleanTitle = (c.title || 'Cupón')
                  .replace(/^(\d+%\s*OFF|\$[\d.,]+\s*OFF)\s*[-–:]?\s*/i, '')
                  .trim() || 'Cupón de Descuento';

                const minText =
                  c.min_vbucks && c.min_vbucks > 0
                    ? `Mín. ${c.min_vbucks.toLocaleString('es-AR')} Pavos`
                    : null;

                const expText = c.expires_at
                  ? `Vence: ${new Date(c.expires_at).toLocaleDateString('es-AR')}`
                  : null;

                return (
                  <div
                    key={c.id || c.code}
                    className={`p-3 bg-[#f8fafc] border border-[var(--line)] flex items-center justify-between gap-3 transition-colors ${
                      c.can_use ? 'hover:border-[#1447e6]' : 'opacity-65'
                    }`}
                    style={{
                      clipPath:
                        'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                    }}
                  >
                    <div className="space-y-1 min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-black text-emerald-800 bg-emerald-100 border border-emerald-300 px-2 py-0.5 rounded font-heading">
                          {discountLabel}
                        </span>
                        <span className="text-xs font-bold text-[#06173f] truncate font-heading">
                          {cleanTitle}
                        </span>
                        {c.is_used ? (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-700 border border-slate-300">
                            Usado
                          </span>
                        ) : c.is_expired ? (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 border border-red-300">
                            Vencido
                          </span>
                        ) : null}
                      </div>

                      <div className="flex items-center gap-2 text-[11px] text-[var(--soft)] font-medium flex-wrap">
                        <span className="font-mono font-bold text-slate-700">{c.code}</span>
                        {(minText || expText) && <span>•</span>}
                        {minText && <span>{minText}</span>}
                        {minText && expText && <span>•</span>}
                        {expText && <span>{expText}</span>}
                      </div>
                    </div>

                    {c.can_use && (
                      <button
                        type="button"
                        onClick={async () => {
                          await useCartStore.getState().applyCoupon(c.code);
                          navigateTo('cart', '/cart');
                        }}
                        className="px-3 py-1.5 bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black text-xs uppercase tracking-wider transition shrink-0 cursor-pointer flex items-center gap-1 shadow-xs active:scale-95"
                        style={{
                          clipPath:
                            'polygon(0 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 4px 100%, 0 calc(100% - 4px))',
                        }}
                      >
                        <span>Usar</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* TARJETA 5: HISTORIAL DE PEDIDOS (IMAGEN 1 1:1) */}
        <div
          id="pedidos"
          data-section="orders-section"
          className="p-5 sm:p-6 bg-white border-2 border-[var(--line)] shadow-sm space-y-4 scroll-mt-24"
          style={{
            clipPath:
              'polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px)',
          }}
        >
          <div className="flex items-center justify-between border-b-2 border-[var(--line)] pb-3">
            <h2 className="text-base font-black uppercase tracking-wider text-[#06173f] flex items-center gap-2 font-heading">
              <Clock className="w-5 h-5 text-[#1447e6]" />
              <span>HISTORIAL DE PEDIDOS</span>
            </h2>
            <button
              type="button"
              onClick={loadOrders}
              disabled={isLoadingOrders}
              className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-[var(--line)] text-slate-500 hover:text-[#06173f] transition cursor-pointer"
              style={{
                clipPath:
                  'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
              }}
              title="Actualizar pedidos"
            >
              <RotateCw className={`w-3.5 h-3.5 ${isLoadingOrders ? 'animate-spin text-[#1447e6]' : ''}`} />
            </button>
          </div>

          {isLoadingOrders && orders.length === 0 ? (
            <div className="py-10 flex flex-col items-center justify-center gap-2.5 text-slate-400">
              <div className="w-8 h-8 border-2 border-[var(--line)] border-t-[#1447e6] rounded-full animate-spin" />
              <span className="text-xs font-bold uppercase tracking-wider text-[var(--soft)] font-heading">
                Cargando pedidos y movimientos...
              </span>
            </div>
          ) : orders.length === 0 ? (
            <div
              className="text-center py-10 bg-[#f8fafc] border border-[var(--line)] p-6 space-y-2"
              style={{
                clipPath:
                  'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
              }}
            >
              <Store className="w-8 h-8 text-slate-400 mx-auto" />
              <p className="text-sm font-bold text-[#06173f] font-heading uppercase">
                Aún no tienes compras ni movimientos
              </p>
              <p className="text-xs text-[var(--soft)] font-medium max-w-sm mx-auto">
                Revisa las skins y lotes disponibles en la tienda de hoy.
              </p>
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => navigateTo('shop', '/tienda')}
                  className="bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black px-4 py-2 uppercase tracking-wider text-xs transition shadow-sm cursor-pointer"
                  style={{
                    clipPath:
                      'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                  }}
                >
                  Ir a la Tienda
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.slice(0, visibleOrdersCount).map((ord) => {
                const cleanCode = ord.order_code.startsWith('#')
                  ? ord.order_code
                  : `#${ord.order_code}`;
                const isAccepted = ord.status === 'COMPLETADO';
                const isCancelled = ord.status === 'CANCELADO' || ord.status === 'EXPIRADO';
                const isPending = ord.status === 'PENDIENTE_PAGO';
                const isPaid = ord.status === 'PAGO_INFORMADO';

                const itemsSummary =
                  ord.items && ord.items.length > 0
                    ? ord.items.length === 1
                      ? ord.items[0].name
                      : `${ord.items[0].name} (+${ord.items.length - 1} más)`
                    : 'Cosméticos de Fortnite';

                const dateFormatted = ord.created_at
                  ? new Date(ord.created_at).toLocaleDateString('es-AR')
                  : 'Reciente';

                return (
                  <div
                    key={ord.order_code}
                    onClick={() => navigateTo('order', `/order.html?code=${encodeURIComponent(ord.order_code)}`)}
                    className={`p-3 sm:p-3.5 bg-white border border-[var(--line)] hover:border-[#1447e6] transition-all cursor-pointer shadow-xs space-y-2 group ${
                      isCancelled ? 'opacity-70' : ''
                    }`}
                    style={{
                      clipPath:
                        'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                    }}
                  >
                    {/* Fila 1: Código + Estado */}
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-xs sm:text-sm font-black text-[#06173f]">
                        {cleanCode}
                      </span>
                      {isPending ? (
                        <span className="text-[10px] font-black uppercase px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 font-heading shrink-0 flex items-center gap-1">
                          <Clock className="w-3 h-3 stroke-[2.5]" />
                          <span>Pendiente</span>
                        </span>
                      ) : isCancelled ? (
                        <span className="text-[10px] font-black uppercase px-2 py-0.5 border border-red-300 bg-red-50 text-red-700 font-heading shrink-0 flex items-center gap-1">
                          <Ban className="w-3 h-3" />
                          <span>Cancelado</span>
                        </span>
                      ) : isAccepted ? (
                        <span className="text-[10px] font-black uppercase px-2 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-800 font-heading shrink-0 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          <span>Entregado</span>
                        </span>
                      ) : isPaid ? (
                        <span className="text-[10px] font-black uppercase px-2 py-0.5 border border-blue-300 bg-blue-50 text-blue-800 font-heading shrink-0 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>Pago informado</span>
                        </span>
                      ) : (
                        <span className="text-[10px] font-black uppercase px-2 py-0.5 border border-slate-300 bg-slate-50 text-slate-700 font-heading shrink-0">
                          {ord.status}
                        </span>
                      )}
                    </div>

                    {/* Fila 2: Ítem resumido y Fecha */}
                    <div className="flex items-center justify-between text-xs gap-2">
                      <span className="font-bold text-[#06173f] truncate">
                        {itemsSummary}
                      </span>
                      <span className="shrink-0 text-[11px] text-[var(--soft)] font-medium">
                        {dateFormatted}
                      </span>
                    </div>

                    {/* Fila 3: Total y Botón de ver detalle */}
                    <div className="flex items-center justify-between pt-2 border-t border-[var(--line)]">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-black text-[#1447e6] font-heading">
                          {ord.currency} $ {Number(ord.total_price || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </span>
                        {ord.total_vbucks ? (
                          <span className="text-[11px] text-[var(--soft)] font-medium">
                            ({ord.total_vbucks.toLocaleString('es-AR')} Pavos)
                          </span>
                        ) : null}
                      </div>

                      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {isPending && ord.payment_method !== 'SALDO_BILLETERA' && (
                          <button
                            type="button"
                            disabled={cancellingCode === ord.order_code}
                            onClick={() => handleCancelOrder(ord.order_code)}
                            title="Cancelar pedido pendiente"
                            className="px-2 py-0.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 text-[10px] font-bold rounded cursor-pointer transition"
                          >
                            Cancelar
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => navigateTo('order', `/order.html?code=${encodeURIComponent(ord.order_code)}`)}
                          title="Ver detalle del pedido"
                          className="text-xs font-bold text-[#1447e6] group-hover:text-[#0a2a9c] flex items-center gap-0.5 cursor-pointer"
                        >
                          <span>Ver orden</span>
                          <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Botón Ver Más - Carga progresiva de a 3 pedidos */}
              {orders.length > visibleOrdersCount && (
                <div className="flex justify-center pt-3">
                  <button
                    type="button"
                    onClick={() => setVisibleOrdersCount((prev) => prev + 3)}
                    className="bg-[#f8fafc] hover:bg-[#eff6ff] text-[#1447e6] border-2 border-[var(--line)] px-6 py-2.5 text-xs font-heading font-black uppercase tracking-wider flex items-center gap-2 transition cursor-pointer shadow-xs active:scale-95"
                    style={{
                      clipPath:
                        'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                    }}
                  >
                    <ChevronDown className="w-3.5 h-3.5 text-[#1447e6]" />
                    <span>Ver más ({orders.length - visibleOrdersCount} restantes)</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={isEditProfileOpen}
        onClose={() => setIsEditProfileOpen(false)}
      />

      {/* Account Status Modal */}
      <AccountStatusModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        account={selectedAccountForStatus}
        onStatusUpdated={() => {
          if (selectedAccountForStatus) {
            checkAccountBadge(selectedAccountForStatus);
          }
        }}
      />

      {/* Modal de Confirmación para Desvincular Cuenta */}
      {accountToUnlink && (
        <div
          className="fixed inset-0 z-[200000] soft-backdrop flex items-center justify-center p-4 overscroll-contain anim-backdrop-in select-none"
          onClick={() => !isUnlinking && setAccountToUnlink(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="unlink-modal-title"
        >
          <div
            className="w-full max-w-md bg-white text-[#06173f] p-5 sm:p-6 border-2 border-[var(--line)] shadow-2xl relative space-y-4 anim-modal-in"
            style={{
              clipPath:
                'polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px)',
              filter: 'drop-shadow(8px 8px 0 rgba(6,23,63,0.25))',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b-2 border-[var(--line)] pb-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 bg-red-50 text-red-600 border border-red-200 flex items-center justify-center font-bold text-base shrink-0"
                  style={{
                    clipPath:
                      'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
                  }}
                >
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3
                    id="unlink-modal-title"
                    className="text-base sm:text-lg font-black uppercase text-[#06173f] font-heading tracking-wide leading-tight"
                  >
                    Desvincular Cuenta
                  </h3>
                  <p className="text-xs text-[var(--soft)] font-medium">
                    Cuentas Vinculadas
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={isUnlinking}
                onClick={() => setAccountToUnlink(null)}
                className="w-8 h-8 rounded-lg bg-[var(--ice)] hover:bg-[#e2e8f0] text-[var(--soft)] hover:text-[#06173f] flex items-center justify-center transition-colors cursor-pointer disabled:opacity-50"
                title="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-3 text-left">
              <p className="text-xs sm:text-sm text-[#06173f] leading-relaxed">
                ¿Estás seguro de que deseas eliminar la cuenta de Fortnite{' '}
                <strong className="text-[#1447e6] font-black uppercase font-mono">
                  {accountToUnlink.username}
                </strong>{' '}
                de tu perfil? Dejará de estar disponible para recibir regalos de la tienda.
              </p>

              {accountToUnlink.is_primary && (
                <div
                  className="p-3 bg-amber-50 border border-amber-300 text-amber-900 text-xs font-semibold space-y-1"
                  style={{
                    clipPath:
                      'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                  }}
                >
                  <span className="font-bold flex items-center gap-1.5 text-amber-800 uppercase font-heading">
                    <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" /> Cuenta Principal
                  </span>
                  <p className="text-[11px] leading-snug">
                    Esta cuenta es actualmente tu cuenta principal. Al eliminarla, otra de tus cuentas pasará a ser la principal de manera automática.
                  </p>
                </div>
              )}

              <p className="text-[11px] text-[var(--soft)] italic">
                Podrás volver a vincularla o agregar una nueva cuenta cuando lo desees.
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-[var(--line)]">
              <button
                type="button"
                disabled={isUnlinking}
                onClick={() => setAccountToUnlink(null)}
                className="px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700 border-2 border-[var(--line)] font-heading font-black text-xs uppercase tracking-wider transition cursor-pointer disabled:opacity-50"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isUnlinking}
                onClick={handleConfirmUnlink}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-heading font-black text-xs uppercase tracking-wider transition flex items-center gap-2 cursor-pointer shadow-md disabled:opacity-50"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                }}
              >
                {isUnlinking ? (
                  <>
                    <RotateCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Desvinculando...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Sí, desvincular cuenta</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Advertencia por Intento de Eliminar Única Cuenta */}
      {singleAccountWarningOpen && (
        <div
          className="fixed inset-0 z-[200000] soft-backdrop flex items-center justify-center p-4 overscroll-contain anim-backdrop-in select-none"
          onClick={() => setSingleAccountWarningOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="single-acc-warning-title"
        >
          <div
            className="w-full max-w-md bg-white text-[#06173f] p-5 sm:p-6 border-2 border-[var(--line)] shadow-2xl relative space-y-4 anim-modal-in"
            style={{
              clipPath:
                'polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px)',
              filter: 'drop-shadow(8px 8px 0 rgba(6,23,63,0.25))',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b-2 border-[var(--line)] pb-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 bg-amber-50 text-amber-600 border border-amber-300 flex items-center justify-center font-bold text-base shrink-0"
                  style={{
                    clipPath:
                      'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
                  }}
                >
                  <Ban className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3
                    id="single-acc-warning-title"
                    className="text-base sm:text-lg font-black uppercase text-[#06173f] font-heading tracking-wide leading-tight"
                  >
                    No se puede desvincular
                  </h3>
                  <p className="text-xs text-[var(--soft)] font-medium">
                    Cuentas de Fortnite
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSingleAccountWarningOpen(false)}
                className="w-8 h-8 rounded-lg bg-[var(--ice)] hover:bg-[#e2e8f0] text-[var(--soft)] hover:text-[#06173f] flex items-center justify-center transition-colors cursor-pointer"
                title="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-3 text-left">
              <div
                className="p-3.5 bg-amber-50 border border-amber-200 text-amber-900 text-xs sm:text-sm font-medium leading-relaxed"
                style={{
                  clipPath:
                    'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                }}
              >
                No puedes eliminar tu única cuenta de Fortnite. Debes tener al menos una cuenta vinculada para poder recibir regalos de la tienda.
              </div>

              <p className="text-xs text-[var(--soft)] leading-relaxed">
                Si deseas cambiar tu cuenta, vincula primero la nueva cuenta de Fortnite y luego podrás desvincular la anterior sin problemas.
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-[var(--line)]">
              <button
                type="button"
                onClick={() => setSingleAccountWarningOpen(false)}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-[var(--line)] font-heading font-black text-xs uppercase tracking-wider transition cursor-pointer"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                }}
              >
                Entendido
              </button>
              <button
                type="button"
                onClick={() => {
                  setSingleAccountWarningOpen(false);
                  openLinkAccount();
                }}
                className="px-5 py-2.5 bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black text-xs uppercase tracking-wider transition flex items-center gap-1.5 cursor-pointer shadow-md"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                }}
              >
                <Plus className="w-3.5 h-3.5 stroke-[3]" />
                <span>Vincular otra cuenta</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
